/**
 * Stone Investment Region Color System
 * 
 * Subtle visual indicators for properties in different regions:
 * - Provence (France): Warm lavender/terracotta tones inspired by lavender fields
 * - Mauritius: Cool ocean teal/turquoise tones inspired by the Indian Ocean
 */

export type Region = 'provence' | 'mauritius' | 'unknown';

export interface RegionColors {
  primary: string;      // Main accent color
  secondary: string;    // Lighter variant
  gradient: string;     // CSS gradient string
  text: string;         // Text color for badges
  background: string;   // Light background tint
}

/**
 * Provence color palette
 * Inspired by lavender fields, terracotta roofs, and golden sunlight
 */
export const provenceColors: RegionColors = {
  primary: '#967BB6',      // Soft lavender
  secondary: '#C8B8D8',    // Light lavender
  gradient: 'linear-gradient(135deg, #967BB6 0%, #C9A86C 100%)',
  text: '#5D4777',         // Deep lavender for text
  background: 'rgba(150, 123, 182, 0.08)', // Very subtle lavender tint
};

/**
 * Mauritius color palette
 * Inspired by turquoise lagoons, coral reefs, and tropical waters
 */
export const mauritiusColors: RegionColors = {
  primary: '#2A9D8F',      // Ocean teal
  secondary: '#6EC6B8',    // Light teal
  gradient: 'linear-gradient(135deg, #2A9D8F 0%, #48CAE4 100%)',
  text: '#1A6B60',         // Deep teal for text
  background: 'rgba(42, 157, 143, 0.08)', // Very subtle teal tint
};

/**
 * Get region colors based on region identifier
 */
export function getRegionColors(region: Region): RegionColors {
  switch (region) {
    case 'provence':
      return provenceColors;
    case 'mauritius':
      return mauritiusColors;
    default:
      // Default to neutral grey for unknown regions
      return {
        primary: '#6b6b6b',
        secondary: '#a0a0a0',
        gradient: 'linear-gradient(135deg, #6b6b6b 0%, #8a8a8a 100%)',
        text: '#4a4a4a',
        background: 'rgba(107, 107, 107, 0.08)',
      };
  }
}

/**
 * Region display names
 */
export const regionNames: Record<Region, { en: string; fr: string }> = {
  provence: { en: 'Provence, France', fr: 'Provence, France' },
  mauritius: { en: 'Mauritius', fr: 'Île Maurice' },
  unknown: { en: 'Location', fr: 'Emplacement' },
};

/**
 * Region icons (emoji-based for simplicity)
 */
export const regionIcons: Record<Region, string> = {
  provence: '🌿', // Lavender/herbs
  mauritius: '🌊', // Ocean
  unknown: '📍',
};

/**
 * Detect region from location string
 * Looks for keywords to determine region
 */
export function detectRegion(location: string | undefined | null): Region {
  if (!location) return 'unknown';
  
  const locationLower = location.toLowerCase();
  
  const provenceKeywords = [
    'provence', 'luberon', 'alpilles', 'var', 'marseille', 'grimaud',
    'aix', 'avignon', 'saint-rémy', 'gordes', 'bonnieux', 'ménerbes',
    'lourmarin', 'cassis', 'bandol', 'saint-tropez', 'france', 'french',
    'côte d\'azur', 'riviera', 'vaucluse', 'bouches-du-rhône',
    ' fr ', 'français', 'francais',
    'paris', 'nice', 'cannes', 'monaco', 'lyon', 'bordeaux', 'toulouse',
    'montpellier', 'nîmes', 'nimes', 'arles', 'antibes', 'grasse', 'menton'
  ];
  
  const mauritiusKeywords = [
    'mauritius', 'maurice', 'mauritian', 'black river', 'rivière noire',
    'tamarin', 'grand baie', 'grand-baie', 'flic en flac', 'belle mare',
    'le morne', 'bel ombre', 'port louis', 'curepipe',
    'indian ocean', 'blue bay', 'trou aux biches', 'pereybere',
    ' mu '
  ];

  // Mauritius investment scheme abbreviations -- match as whole words only
  // to avoid false positives (e.g. "res" in "residence", "irs" in "first")
  const mauritiusAcronyms = [/\bpds\b/, /\birs\b/, /\bres\b/];
  
  for (const keyword of provenceKeywords) {
    if (locationLower.includes(keyword)) return 'provence';
  }
  
  for (const keyword of mauritiusKeywords) {
    if (locationLower.includes(keyword)) return 'mauritius';
  }

  for (const pattern of mauritiusAcronyms) {
    if (pattern.test(locationLower)) return 'mauritius';
  }
  
  return 'unknown';
}

/**
 * Get region flag emoji
 */
export function getRegionFlag(region: Region): string {
  switch (region) {
    case 'provence':
      return '🇫🇷';
    case 'mauritius':
      return '🇲🇺';
    default:
      return '🌍';
  }
}
